import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Building2, User, Calendar, CreditCard, MessageSquare, Copy, CheckCircle, AlertTriangle, Link2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteClinicModal({ open, onClose, onSuccess, pricingTiers }) {
  const [loading, setLoading] = useState(false);
  const [invitationResult, setInvitationResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    clinic_name: '',
    subdomain: '',
    admin_email: '',
    admin_name: '',
    contact_phone: '',
    address: '',
    subscription_tier_id: null,
    trial_days: 30,
    custom_message: ''
  });

  useEffect(() => {
    // Auto-generate subdomain from clinic name
    if (formData.clinic_name && !formData.subdomain) {
      let slug = formData.clinic_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      
      // Ensure subdomain is within 3-20 character limit
      if (slug.length > 20) {
        slug = slug.substring(0, 20).replace(/-$/, '');
      }
      if (slug.length < 3 && slug.length > 0) {
        slug = slug + 'clinic';
      }
      
      setFormData(prev => ({ ...prev, subdomain: slug }));
    }
  }, [formData.clinic_name, formData.subdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.clinic_name.trim()) {
      toast.error('Please enter clinic name');
      return;
    }
    if (!formData.subdomain.trim()) {
      toast.error('Please enter subdomain');
      return;
    }
    if (!formData.admin_email.trim() || !formData.admin_email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.admin_name.trim()) {
      toast.error('Please enter admin name');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/super-admin/invitations/create`,
        formData
      );

      if (response.data.success) {
        // Check if email was actually sent
        if (response.data.email_sent) {
          toast.success('Invitation sent successfully! 🎉');
        } else {
          toast.warning('Invitation created but email could not be sent. Please copy the link manually.');
        }
        
        // Store the result to show the invitation URL
        setInvitationResult(response.data);
        
        // Reset form
        setFormData({
          clinic_name: '',
          subdomain: '',
          admin_email: '',
          admin_name: '',
          contact_phone: '',
          address: '',
          subscription_tier_id: null,
          trial_days: 30,
          custom_message: ''
        });
        
        onSuccess(response.data);
        
        // Only auto-close if email was sent successfully
        if (response.data.email_sent) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationUrl = () => {
    if (invitationResult?.invitation_url) {
      navigator.clipboard.writeText(invitationResult.invitation_url);
      setCopied(true);
      toast.success('Invitation link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setInvitationResult(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Mail className="h-6 w-6 text-purple-600" />
            Invite New Clinic
          </DialogTitle>
          <DialogDescription>
            Send an invitation email to onboard a new clinic with pre-configured settings
          </DialogDescription>
        </DialogHeader>

        {/* Show Invitation URL when email fails */}
        {invitationResult && !invitationResult.email_sent && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800">Email Could Not Be Sent</h4>
                <p className="text-sm text-amber-700">
                  The invitation was created but the email could not be delivered. 
                  Please copy the invitation link below and send it manually.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-amber-800 flex items-center gap-1">
                <Link2 className="h-4 w-4" />
                Invitation Link
              </Label>
              <div className="flex gap-2">
                <Input 
                  value={invitationResult.invitation_url} 
                  readOnly 
                  className="font-mono text-sm bg-white"
                />
                <Button 
                  type="button" 
                  onClick={copyInvitationUrl}
                  variant={copied ? "default" : "outline"}
                  className={copied ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  {copied ? (
                    <><CheckCircle className="h-4 w-4 mr-1" /> Copied!</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-amber-600">
                Send this link to: <strong>{formData.admin_email || 'the clinic admin'}</strong>
              </p>
            </div>
            
            <Button 
              type="button" 
              onClick={handleClose}
              className="w-full mt-2"
            >
              Done
            </Button>
          </div>
        )}

        {/* Show form only if no result yet */}
        {!invitationResult && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Clinic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              Clinic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="clinic_name">Clinic Name *</Label>
                <Input
                  id="clinic_name"
                  placeholder="e.g., Wellness Medical Center"
                  value={formData.clinic_name}
                  onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subdomain">Subdomain *</Label>
                <Input
                  id="subdomain"
                  placeholder="e.g., wellness-medical"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500">3-20 characters, lowercase letters, numbers, hyphens only</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone Number</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Admin User Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Admin User
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin_name">Admin Full Name *</Label>
                <Input
                  id="admin_name"
                  placeholder="Dr. John Smith"
                  value={formData.admin_name}
                  onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_email">Admin Email *</Label>
                <Input
                  id="admin_email"
                  type="email"
                  placeholder="admin@clinic.com"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  required
                />
              </div>
            </div>
          </div>

          {/* Subscription Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              Subscription Settings
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subscription_tier_id">Subscription Tier</Label>
                <Select
                  value={formData.subscription_tier_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, subscription_tier_id: value === "none" ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No tier (Free Trial only)</SelectItem>
                    {pricingTiers.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} - ${tier.monthly_price}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="trial_days">Trial Period (Days) *</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="7"
                  max="90"
                  value={formData.trial_days}
                  onChange={(e) => setFormData({ ...formData, trial_days: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>
          </div>

          {/* Custom Message */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              Personal Message (Optional)
            </h3>
            
            <div className="space-y-2">
              <Textarea
                id="custom_message"
                placeholder="Add a personal message to include in the invitation email..."
                value={formData.custom_message}
                onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
                rows={3}
              />
              <p className="text-xs text-gray-500">
                This message will be included in the invitation email
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
